<template>
  <!-- jcs-portal-card + jcs-portal-reports = the shared /reports/ shell (full-bleed report container, same as
       CustomerInsights.vue) — driven by steele-consistency-fix.css `#routeView .jcs-portal-reports > .container`. -->
  <div class="jcs-portal-card jcs-portal-reports">
    <div class="container">
      <!-- Branded content panel: white card with a brand-red top line (like the portal's card stripe / the
           report cards in CustomerInsights.vue). Header centered; below it the report cards stack in a LEFT
           column with the "What's New" changelog filling the right (user layout request 2026-07-31). -->
      <div class="rd-panel">
        <div class="rd-head">
          <h1 class="rd-title">Reports</h1>
          <p class="rd-sub">
            Need access or a feature to any report? Email your request to the helpdesk by
            <button type="button" class="rd-sub-link" @click="openRequest">clicking here</button>.
          </p>
        </div>

        <div v-if="loading" class="rd-loading">Loading your reports…</div>

        <div v-else-if="visibleReports.length" class="rd-body">
          <div class="rd-list">
            <button
              v-for="r in visibleReports"
              :key="r.key"
              type="button"
              class="rd-card"
              :class="{ 'has-pop': memberPop === r.key }"
              @click="openReport(r.key)"
              @mouseleave="memberPop === r.key && (memberPop = '')"
            >
              <span class="rd-card-ic" aria-hidden="true">{{ r.icon }}</span>
              <span class="rd-card-body">
                <span class="rd-card-title">{{ r.title }}</span>
                <span class="rd-card-desc">{{ r.desc }}</span>
              </span>
              <!-- 👥 who-can-see-this. Renders only when the Master-gated members fetch succeeded, so the
                   server stays the boundary — a non-Master never even sees the affordance. click.stop:
                   the icon lives INSIDE the open-report button. -->
              <span v-if="membersOk" class="rd-mem-ic" :title="'Who can see ' + r.title"
                    @mouseenter="memberPop = r.key" @click.stop="memberPop = memberPop === r.key ? '' : r.key">👥</span>
              <span class="rd-card-go" aria-hidden="true">Open&nbsp;→</span>
              <span v-if="memberPop === r.key && membersOk" class="rd-mem-pop" @click.stop>
                <span class="rd-mem-pop-h">Who can see {{ r.title }} <em>{{ (members[r.key] || []).length }}</em></span>
                <span v-if="(members[r.key] || []).length" class="rd-mem-pop-list">
                  <span v-for="(m, mi) in members[r.key]" :key="'mm' + r.key + mi" class="rd-mem-pop-item" :class="{ 'is-warn': m.tag === 'warn', 'is-all': m.tag === 'all' }">
                    {{ m.e }}<em v-if="m.tag === 'warn'" class="rd-mem-warn-tag"
                        title="Assigned a territory but on NO report-access list — the report won't open for them until a Master adds them under Account Setup → Report Access.">⚠ territory only</em><em
                        v-else-if="m.tag === 'all'" class="rd-mem-all-tag"
                        title="On the All Insights list — they already hold the role, or it's granted automatically at their next Microsoft sign-in. Every report opens for them.">✓ All Insights</em>
                  </span>
                </span>
                <span v-else class="rd-mem-pop-none">Nobody is listed yet — grant access in Account Setup → Report Access.</span>
                <span v-if="r.key === 'sales-insights'" class="rd-mem-pop-foot">Includes Territory Access members — reps see Sales Insights through their territories.</span>
                <span v-if="memberAlls(r.key)" class="rd-mem-pop-foot rd-mem-pop-allfoot">✓ {{ memberAlls(r.key) }} member(s) come from the All Insights list — every report opens for them (the role is granted automatically at their next Microsoft sign-in if they haven't signed in since being added).</span>
                <span v-if="memberWarns(r.key)" class="rd-mem-pop-foot rd-mem-pop-warnfoot"
                      :title="'Missing from the list: ' + memberWarnList(r.key)">
                  ⚠ {{ memberWarns(r.key) }} member(s) have a territory but are on NO report-access list — the report won't open for them until they're added in Account Setup → Report Access (Masters excepted).
                  <button type="button" class="rd-mem-copy"
                          :title="'Copy the ' + memberWarns(r.key) + ' missing address(es), comma-separated — paste them into Report Access → Sales Insights → manual emails'"
                          @click.stop="copyWarnMembers(r.key)">{{ memberCopied === r.key ? "✓ copied" : "⧉ copy" }}</button>
                </span>
                <span class="rd-mem-pop-foot">Mirrors Account Setup → Report Access — ✓/⚠ tags mark inherited members. Masters and “All Insights” members always have access even when not listed here.</span>
              </span>
            </button>
          </div>

          <!-- What's New — grouped per report, newest first. Items come from rpt_ReportNews
               (GET /report/report-news) so helpdesk@jcsteele.com adds/edits/deletes them RIGHT HERE and the
               full history survives deploys; until that table exists the baked-in REPORT_NEWS list renders
               read-only. Each group shows its NEWEST item only (NEWS_COLLAPSED) with "Show N more"; each
             body is clamped to 3 lines with a "Read more" toggle; the search box filters ALL news. -->
          <div class="rd-news">
            <div class="rd-news-top">
              <div>
                <div class="rd-news-h">✨ What's New</div>
                <p class="rd-news-sub">The latest changes inside your reports.</p>
              </div>
              <button v-if="canEditNews" type="button" class="rd-news-trashbtn" title="Deleted news items — view and restore" @click="openNewsTrash">🗑 Deleted</button>
              <input v-model.trim="newsSearch" type="text" class="rd-news-search" placeholder="Search all news…" />
            </div>
            <!-- ONE hidden picker shared by both editor blocks (only one editor is ever open). No accept
                 filter on purpose — the 📎 takes any format (user 2026-08-01). -->
            <input ref="newsFile" type="file" multiple class="rd-news-fileinput" @change="onNewsFilePick" />
            <!-- Attachment preview modal — MODAL FIRST (user 2026-08-01): images get the Search chart
                 viewer feel (wheel zoom, drag pan); other formats get name + actions. Download streams
                 through the API (blob container is cross-origin — a plain link can't force save-as);
                 Open shows the raw blob in a new tab. Backdrop/✕ closes. -->
            <!-- Delete confirm — "Are you sure?" before an item moves to the Deleted history (user
                 2026-08-01). Backdrop/✕/Cancel all back out. -->
            <div v-if="newsDelConfirm" class="rd-att-overlay" role="dialog" aria-modal="true" @click.self="newsDelConfirm = null">
              <div class="rd-confirm-modal">
                <div class="rd-att-head">
                  <span class="rd-att-title">Delete this news item?</span>
                  <button type="button" class="rd-att-x" @click="newsDelConfirm = null" aria-label="Close">✕</button>
                </div>
                <div class="rd-confirm-body">
                  <span class="rd-news-date">{{ newsDelConfirm.d ? fmtNewsDate(newsDelConfirm.d) : "—" }}</span>
                  <span class="rd-confirm-text">{{ newsDelConfirm.t }}</span>
                  <span v-if="newsDelConfirm.a && newsDelConfirm.a.length" class="rd-confirm-atts">📎 {{ newsDelConfirm.a.length }} attachment{{ newsDelConfirm.a.length > 1 ? "s" : "" }} go with it</span>
                  <span class="rd-confirm-hint">It moves to the <b>🗑 Deleted</b> history — you can restore it from there anytime.</span>
                </div>
                <div class="rd-att-foot">
                  <span class="rd-att-btns">
                    <button type="button" class="rd-att-open" :disabled="newsDelBusy" @click="newsDelConfirm = null">Cancel</button>
                    <button type="button" class="rd-att-dl" :disabled="newsDelBusy" @click="confirmDeleteNews">{{ newsDelBusy ? "Deleting…" : "Yes, delete" }}</button>
                  </span>
                </div>
              </div>
            </div>
            <!-- Deleted history — gated exactly like editing; restore puts the item straight back. -->
            <div v-if="newsTrashOpen" class="rd-att-overlay" role="dialog" aria-modal="true" @click.self="newsTrashOpen = false">
              <div class="rd-confirm-modal rd-trash-modal">
                <div class="rd-att-head">
                  <span class="rd-att-title">🗑 Deleted news items</span>
                  <button type="button" class="rd-att-x" @click="newsTrashOpen = false" aria-label="Close">✕</button>
                </div>
                <div class="rd-trash-body">
                  <p v-if="newsTrashBusy" class="rd-trash-note">Loading…</p>
                  <p v-else-if="newsTrashErr" class="rd-trash-note">{{ newsTrashErr }}</p>
                  <p v-else-if="!newsTrashItems.length" class="rd-trash-note">Nothing here — deleted news items will show up for restoring.</p>
                  <div v-for="it in newsTrashItems" :key="'tr' + it.id" class="rd-trash-item">
                    <div class="rd-trash-meta">
                      <span class="rd-news-date">{{ it.newsDate ? fmtNewsDate(it.newsDate) : "—" }}</span>
                      <span class="rd-trash-report">{{ newsReportTitle(it.reportKey) }}</span>
                      <span class="rd-trash-when">deleted {{ it.deletedOn }}</span>
                      <button type="button" class="rd-trash-restore" :disabled="newsTrashBusy" @click="restoreNews(it)">↺ Restore</button>
                    </div>
                    <div class="rd-trash-text">{{ it.body }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="newsPreview" class="rd-att-overlay" role="dialog" aria-modal="true" @click.self="closeNewsPreview">
              <div class="rd-att-modal">
                <div class="rd-att-head">
                  <span class="rd-att-title" :title="newsPreview.name">{{ newsPreview.isImage ? "🖼" : "📎" }} {{ newsPreview.name || "Attachment" }}</span>
                  <button type="button" class="rd-att-x" @click="closeNewsPreview" aria-label="Close">✕</button>
                </div>
                <div v-if="newsPreview.isImage" class="rd-att-body"
                     @wheel.prevent="newsPvWheel"
                     @mousedown.prevent="newsPvPanStart" @mousemove="newsPvPanMove"
                     @mouseup="newsPvPanEnd" @mouseleave="newsPvPanEnd">
                  <img :src="newsPreview.url" class="rd-att-img" :alt="newsPreview.name || 'attachment'" draggable="false"
                       :style="{ transform: 'translate(' + newsPvX + 'px,' + newsPvY + 'px) scale(' + newsPvScale + ')' }" />
                </div>
                <div v-else class="rd-att-body rd-att-nofile">
                  <span class="rd-att-bigicon" aria-hidden="true">📎</span>
                  <span>No preview for this file type — download it, or open it in a new tab.</span>
                </div>
                <div class="rd-att-foot">
                  <span v-if="newsPreview.isImage" class="rd-att-hint">Scroll to zoom · drag to pan</span>
                  <span class="rd-att-btns">
                    <button type="button" class="rd-att-dl" :disabled="newsPvBusy" @click="downloadNewsAttachment(newsPreview)">{{ newsPvBusy ? "Downloading…" : "⬇ Download" }}</button>
                    <button type="button" class="rd-att-open" @click="openNewsAttachmentTab(newsPreview)">↗ Open</button>
                  </span>
                </div>
              </div>
            </div>
            <div v-for="g in newsGroups" :key="'ng' + g.key" class="rd-news-group">
              <div class="rd-news-group-h">
                <span class="rd-news-group-ic" aria-hidden="true">{{ g.icon }}</span>
                <span>{{ g.title }}</span>
                <button v-if="canEditNews" type="button" class="rd-news-add" title="Add a news item" @click="startNewsEdit(g.key, null)">＋</button>
              </div>
              <!-- the ＋ add form opens at the TOP of its category (user 2026-07-31) -->
              <div v-if="canEditNews && newsEdit && !newsEdit.id && newsEdit.key === g.key" class="rd-news-item">
                  <span class="rd-news-editwrap">
                    <span class="rd-news-erow">
                      <input type="date" v-model="newsEdit.d" class="rd-news-edate" />
                      <span class="rd-news-ebtns">
                        <button type="button" class="rd-news-esave" :disabled="newsBusy || !newsEdit.t.trim()" @click="saveNews">{{ newsBusy ? "Saving…" : "Save" }}</button>
                        <button type="button" class="rd-news-ecancel" :disabled="newsBusy" @click="newsEdit = null">Cancel</button>
                      </span>
                    </span>
                    <textarea v-model="newsEdit.t" rows="3" class="rd-news-etext"
                              placeholder="What changed? (paste a screenshot right here with Ctrl+V)"
                              @paste="onNewsPaste"></textarea>
                    <span class="rd-news-attrow">
                      <button type="button" class="rd-news-clip" :disabled="newsAttBusy" @click="$refs.newsFile.click()"
                              title="Attach files of any format — or paste a screenshot (Ctrl+V) straight into the text box">📎 Attach</button>
                      <span v-if="newsAttBusy" class="rd-news-attbusy">Uploading…</span>
                      <span v-for="(a, ai) in newsEdit.a" :key="'ne' + ai" class="rd-news-attchip" :title="a.name">
                        <img v-if="a.isImage" :src="a.url" class="rd-news-attchipimg" alt="" />
                        <span v-else aria-hidden="true">📎</span>
                        <span class="rd-news-attchipname">{{ a.name }}</span>
                        <button type="button" class="rd-news-attchipx" title="Remove this attachment" @click="newsEdit.a.splice(ai, 1)">×</button>
                      </span>
                    </span>
                    <span v-if="newsErr" class="rd-news-err">{{ newsErr }}</span>
                  </span>
              </div>
              <div v-for="(n, i) in g.shown" :key="'ni' + g.key + (n.id || i)" class="rd-news-item">
                <template v-if="canEditNews && newsEdit && newsEdit.id && n.id === newsEdit.id">
                  <span class="rd-news-editwrap">
                    <span class="rd-news-erow">
                      <input type="date" v-model="newsEdit.d" class="rd-news-edate" />
                      <span class="rd-news-ebtns">
                        <button type="button" class="rd-news-esave" :disabled="newsBusy || !newsEdit.t.trim()" @click="saveNews">{{ newsBusy ? "Saving…" : "Save" }}</button>
                        <button type="button" class="rd-news-ecancel" :disabled="newsBusy" @click="newsEdit = null">Cancel</button>
                      </span>
                    </span>
                    <textarea v-model="newsEdit.t" rows="3" class="rd-news-etext"
                              placeholder="What changed? (paste a screenshot right here with Ctrl+V)"
                              @paste="onNewsPaste"></textarea>
                    <span class="rd-news-attrow">
                      <button type="button" class="rd-news-clip" :disabled="newsAttBusy" @click="$refs.newsFile.click()"
                              title="Attach files of any format — or paste a screenshot (Ctrl+V) straight into the text box">📎 Attach</button>
                      <span v-if="newsAttBusy" class="rd-news-attbusy">Uploading…</span>
                      <span v-for="(a, ai) in newsEdit.a" :key="'ne' + ai" class="rd-news-attchip" :title="a.name">
                        <img v-if="a.isImage" :src="a.url" class="rd-news-attchipimg" alt="" />
                        <span v-else aria-hidden="true">📎</span>
                        <span class="rd-news-attchipname">{{ a.name }}</span>
                        <button type="button" class="rd-news-attchipx" title="Remove this attachment" @click="newsEdit.a.splice(ai, 1)">×</button>
                      </span>
                    </span>
                    <span v-if="newsErr" class="rd-news-err">{{ newsErr }}</span>
                  </span>
                </template>
                <template v-else>
                  <span class="rd-news-date">{{ n.d ? fmtNewsDate(n.d) : "—" }}</span>
                  <span class="rd-news-text">
                    <!-- Body clamped to 3 lines. It MUST be its own element: the attachments live in this
                         same .rd-news-text, and clamping the wrapper would hide them too. -->
                    <span class="rd-news-body" :class="{ 'is-clamped': !newsTextOpen[nk(n)] }"
                          :data-nk="nk(n)" ref="newsBody">{{ n.t }}</span>
                    <!-- Shown only when the text actually overflows 3 lines (measured, not guessed), and
                         kept while open so there is always a way back. -->
                    <button v-if="newsTextClamped[nk(n)] || newsTextOpen[nk(n)]" type="button"
                            class="rd-news-readmore" @click="$set(newsTextOpen, nk(n), !newsTextOpen[nk(n)])">
                      {{ newsTextOpen[nk(n)] ? "Show less" : "Read more" }}
                    </button>
                    <!-- Attachments — COLLAPSED by default, expanded per item on click (user 2026-08-01). -->
                    <span v-if="n.a && n.a.length" class="rd-news-attwrap">
                      <button type="button" class="rd-news-atttoggle"
                              @click="$set(newsExpandedAtt, n.id, !newsExpandedAtt[n.id])">
                        📎 {{ n.a.length }} attachment{{ n.a.length > 1 ? "s" : "" }} {{ newsExpandedAtt[n.id] ? "▾" : "▸" }}
                      </button>
                      <!-- MODAL FIRST (user 2026-08-01): every attachment opens the preview modal below —
                           Download / Open-in-new-tab are offered from there, never directly. -->
                      <span v-if="newsExpandedAtt[n.id]" class="rd-news-attlist">
                        <template v-for="(a, ai) in n.a">
                          <img v-if="a.isImage" :key="'nai' + n.id + '_' + ai" :src="a.url" class="rd-news-attimg"
                               :alt="a.name || 'attachment'" loading="lazy" :title="'Preview ' + (a.name || 'image')"
                               @click="openNewsPreview(a)" />
                          <button v-else :key="'naf' + n.id + '_' + ai" type="button" class="rd-news-attfile"
                                  :title="'Preview / download ' + (a.name || 'attachment')"
                                  @click="openNewsPreview(a)">📎 {{ a.name || "attachment" }}</button>
                        </template>
                      </span>
                    </span>
                  </span>
                  <span v-if="canEditNews && n.id" class="rd-news-act">
                    <button type="button" class="rd-news-ib" title="Edit" @click="startNewsEdit(g.key, n)">✎</button>
                    <button type="button" class="rd-news-ib rd-news-ib-x" title="Delete" @click="deleteNews(n)">×</button>
                  </span>
                </template>
              </div>
              <button v-if="g.canToggle" type="button" class="rd-news-more" @click="toggleNewsGroup(g.key)">
                {{ g.expanded ? "Show less" : "Show " + g.hiddenCount + " more" }}
              </button>
            </div>
            <p v-if="!newsGroups.length" class="rd-news-none">{{ newsSearch ? "No news matches your search." : "No recent changes for your reports." }}</p>
          </div>
        </div>

        <div v-else class="rd-empty">
          <span class="rd-empty-ic" aria-hidden="true">🔒</span>
          <p>
            You don't have access to any reports yet. A Master can grant it in
            <b>Account&nbsp;Setup&nbsp;→&nbsp;Report&nbsp;Access</b>.
          </p>
        </div>

        <!-- "Need access or a feature?" → helpdesk email. Same machinery as Search's report-chart
             modal (EmailController → helpdesk@jcsteele.com, ReplyTo = the requester), but open to every
             signed-in user — someone WITHOUT access is exactly who needs to ask. -->
        <div v-if="reqOpen" class="rd-req-overlay" @click.self="closeRequest">
          <div class="rd-req-modal" role="dialog" aria-modal="true">
            <div class="rd-req-head">
              <span>Request report access or a feature</span>
              <button type="button" class="rd-req-x" @click="closeRequest" aria-label="Close">✕</button>
            </div>
            <template v-if="!reqDone">
              <p class="rd-req-sub">This emails the Help Desk with your name and reply-to address attached — describe what you need and they'll follow up.</p>
              <textarea v-model="reqMsg" rows="5" class="rd-req-text" placeholder="e.g. Please give me access to Foundry Insights — or: could Sales Insights add a monthly-trend card?"></textarea>
              <p v-if="reqErr" class="rd-req-err">{{ reqErr }}</p>
              <div class="rd-req-actions">
                <button type="button" class="rd-req-cancel" :disabled="reqBusy" @click="closeRequest">Cancel</button>
                <button type="button" class="rd-req-send" :disabled="reqBusy || !reqMsg.trim()" @click="sendRequest">{{ reqBusy ? "Sending…" : "Send to Helpdesk" }}</button>
              </div>
            </template>
            <template v-else>
              <p class="rd-req-done">✓ Sent — the Help Desk has your request and will reply to you by email.</p>
              <div class="rd-req-actions">
                <button type="button" class="rd-req-send" @click="closeRequest">Close</button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import httpClient from "@/assets/js/HttpClient";

// The reports available on the portal. Visibility is governed per-user by Report Access
// (rpt_UserReportAccess via /report/access/mine); a Master sees them all. Each card deep-links
// into the Sales Insights page with ?report=<key>, which selects the matching report layout.
// How many news items each report group shows before "Show N more". 1 = newest only
// (user 2026-08-05). Referenced in newsGroups AND the button label, so it lives here rather
// than being retyped as a literal in three places the way "3" was.
const NEWS_COLLAPSED = 1;

const REPORTS = [
  {
    key: "sales-insights",
    title: "Sales Insights",
    icon: "📊",
    desc: "Orders, customers, territories, saved carts and AI coaching — the full sales analytics dashboard."
  },
  {
    key: "manufacturing-insights",
    title: "Manufacturing Insights",
    icon: "🏭",
    desc: "Production-focused reporting, curated for the manufacturing team."
  },
  {
    key: "foundry-insights",
    title: "Foundry Insights",
    icon: "🔥",
    desc: "Foundry production — shipped weight by alloy, parts sales, labor (HRS/ton, overtime) and scrap."
  },
  {
    key: "work-order-insights",
    title: "Work Order Insights",
    icon: "📋",
    desc: "Work order throughput, open-WO aging & past-due, cost variance, work-center load and sub-WO dependencies."
  },
  {
    key: "accounting-insights",
    title: "Accounting",
    icon: "🧾",
    desc: "Parts sold at a loss by date — lines invoiced below the actual cost booked at shipment — plus current part costing."
  }
];

// FALLBACK "What's New" list — used ONLY until rpt_ReportNews exists (GET /report/report-news answers
// available:false), rendered read-only. Once AI/report-news.sql runs, the DB is authoritative: it is
// seeded with the FULL history (a superset of this list) and helpdesk@jcsteele.com edits items on the
// page itself. Do not grow this list — append new entries to the SQL seed / via the page instead.
const REPORT_NEWS = {
  "sales-insights": [
    { d: "2026-07-31", t: "The chat bar now stays on screen while you scroll — with a slow-orbiting border — and a maximized card tucks neatly beneath it." },
    { d: "2026-07-30", t: "Costing for Parts: unit price and current-margin columns, expandable rows with cost history, a one-click below-cost filter, search suggestions from the whole database with multi-term chips, a four-shape chart cycle, printable presentation, and Excel with embedded charts." },
    { d: "2026-07-22", t: "“Tell me what you want to see” — the Ask bar is available to every report role, understands every filter (including machine codes), and chains apply → favorite → email digest. Voice input included." },
    { d: "2026-07-08", t: "Orders In Process card with a full order preview, and 💡 per-part sales-edge insights on saved orders and quotes." }
  ],
  "accounting-insights": [
    { d: "2026-07-31", t: "New report: Parts Sold at a Loss by Date — every shipment line invoiced at or below the ACTUAL cost booked on the shipment date, split into Below cost / No charge / Credits — plus current part costing. Search chips, charts, AI insights, a printable presentation and Excel export." }
  ],
  "manufacturing-insights": [
    { d: "2026-07-14", t: "Now a locked standard report governed by Report Access — a Master grants it by email address or Entra group." }
  ],
  "foundry-insights": [
    { t: "Initial release — shipped weight by alloy, parts sales, labor (HRS/ton, overtime) and scrap." }
  ],
  "work-order-insights": [
    { t: "Initial release — work order throughput, open-WO aging & past-due, cost variance, work-center load and sub-WO dependencies." }
  ]
};

export default {
  name: "ReportsDashboard",
  // Route passes { default:true, admin:false } (customer site shell). Like CustomerInsights, we don't declare
  // them as props — they fall through to $attrs, exactly matching the proven sibling page's shell integration.
  data() {
    return {
      reports: REPORTS,
      // Published display names for the shared reports, keyed by rpt_RoleDefaultLayout row. helpdesk can
      // rename a report from the Sales Insights drawer; the tile must follow or the two disagree.
      sharedNames: {},
      authorized: [],   // report keys this user may see (from the server)
      ok: false,        // true only after a SUCCESSFUL access fetch
      loading: true,
      // 👥 members per report (Master only — built from the Master-gated report-users/territory-users
      // endpoints; a 403 leaves membersOk false and the icons never render).
      members: {},      // report key -> sorted [emails]
      membersOk: false,
      memberPop: "",    // report key whose popup is open (hover/click)
      memberCopied: "", // report key whose ⚠ list was just copied (flashes "✓ copied")
      // What's New state. newsDb flips true when rpt_ReportNews answered — until then the baked-in
      // fallback renders read-only (canEditNews stays false so no edit affordances can appear).
      newsItems: [],    // DB rows: [{ id, reportKey, newsDate, body }]
      newsDb: false,
      newsSearch: "",
      newsExpanded: {}, // report key -> true when "Show more" is open
      newsTextOpen: {},    // news key -> true when its body is expanded past the 3-line clamp
      newsTextClamped: {}, // news key -> true when its body actually overflows 3 lines (measured)
      newsEdit: null,   // { id|null, key, d, t, a: [{url,name,isImage}] } — null id = adding
      newsBusy: false,
      newsAttBusy: false,   // an attachment upload is in flight (Ctrl+V paste or 📎 pick)
      newsExpandedAtt: {},  // news item id -> attachments expanded (COLLAPSED by default — user 2026-08-01)
      newsPreview: null,    // attachment open in the preview modal ({url,name,isImage}) — modal FIRST, always
      newsPvScale: 1,       // preview zoom (wheel, 1–6, multiplicative like the Search chart viewer)
      newsPvX: 0,           // preview pan offsets (drag)
      newsPvY: 0,
      newsPvBusy: false,    // download-through-the-API in flight
      newsDelConfirm: null, // news item pending the "Are you sure?" delete confirm (user 2026-08-01)
      newsDelBusy: false,
      newsTrashOpen: false, // 🗑 Deleted history modal
      newsTrashItems: [],
      newsTrashBusy: false,
      newsTrashErr: "",
      // "Need access or a feature?" helpdesk-email modal
      reqOpen: false,
      reqMsg: "",
      reqBusy: false,
      reqDone: false,
      reqErr: "",
      newsErr: ""
    };
  },
  computed: {
    visibleReports() {
      // Fail open until access resolves (or if the endpoint isn't deployed yet): show all, and let the report
      // page + its server-side [Authorize]/scoping enforce the real boundary. Once access is known, filter.
      if (!this.ok) return this.withNames(this.reports);
      // "all-insights" is the COVERING list — its role satisfies every report gate, the way Master does.
      // Being on it means EVERY tile; without this a user granted only All Insights saw an empty page.
      if (this.authorized.indexOf("all-insights") >= 0) return this.withNames(this.reports);
      return this.withNames(this.reports.filter((r) => this.authorized.indexOf(r.key) >= 0));
    },
    // DB rows regrouped to the render shape. Falls back to the baked-in list until the table exists.
    newsItemsByKey() {
      if (!this.newsDb) return REPORT_NEWS;
      const out = {};
      (this.newsItems || []).forEach((n) => {
        const k = String(n.reportKey || "").toLowerCase();
        let a = [];
        if (n.attachments) { try { a = JSON.parse(n.attachments) || []; } catch (e) { a = []; } }
        (out[k] = out[k] || []).push({ id: n.id, d: n.newsDate || "", t: n.body, a });
      });
      return out;
    },
    // Only helpdesk@jcsteele.com AS Master edits the changelog — the same publisher identity that owns the
    // shared standard layouts. UX only: POST/DELETE re-check the email claim server-side.
    canEditNews() {
      if (!this.newsDb) return false;   // the baked-in fallback is read-only by definition
      const g = (this.$store && this.$store.getters) ? this.$store.getters : {};
      if (g.effectiveRoleName !== "Master") return false;
      const ru = g.realUser;
      return String((ru && ru.email) || "").trim().toLowerCase() === "helpdesk@jcsteele.com";
    },
    // What's New groups: only the reports THIS user can open (same access filter as the tiles), only those
    // with entries. Newest 3 per group unless expanded; an active search shows EVERY match and ignores the
    // cap. Groups ordered by their newest entry (undated groups last) so fresh news reads first.
    newsGroups() {
      const q = (this.newsSearch || "").toLowerCase();
      const source = this.newsItemsByKey;
      const groups = this.visibleReports
        .map((r) => {
          let items = (source[r.key] || []).slice();
          items.sort((a, b) => ((b.d || "") > (a.d || "") ? 1 : (b.d || "") < (a.d || "") ? -1 : 0));
          if (q) items = items.filter((n) => (n.t || "").toLowerCase().includes(q) || r.title.toLowerCase().includes(q));
          const expanded = !!this.newsExpanded[r.key] || !!q;
          const newestD = items.reduce((m, n) => (n.d && n.d > m ? n.d : m), "");
          return {
            key: r.key, title: r.title, icon: r.icon, newestD,
            total: items.length,
            shown: expanded ? items : items.slice(0, NEWS_COLLAPSED),
            hiddenCount: Math.max(0, items.length - NEWS_COLLAPSED),
            canToggle: items.length > NEWS_COLLAPSED && !q,
            expanded
          };
        })
        .filter((g) => g.total > 0);
      return groups.sort((a, b) => (b.newestD > a.newestD ? 1 : b.newestD < a.newestD ? -1 : 0));
    }
  },
  watch: {
    // Re-measure whenever the rendered news changes. measureNewsClamp only writes on a real change,
    // so this cannot loop through the watcher.
    newsGroups() { this.$nextTick(this.measureNewsClamp); }
  },
  mounted() {
    const self = this;
    // Shared-report display names. Best-effort: on failure the built-in titles stand.
    httpClient
      .get("/report/role-default-layout")
      .then((res) => {
        const roles = (res.data && res.data.roles) || {};
        const out = {};
        Object.keys(roles).forEach((role) => {
          try { const d = JSON.parse(roles[role]); if (d && typeof d.name === "string" && d.name.trim()) out[role] = d.name.trim(); } catch (e) { /* skip */ }
        });
        self.sharedNames = out;
      })
      .catch(() => { /* keep built-in titles */ });
    httpClient
      .get("/report/access/mine")
      .then((res) => {
        const list = (res.data && res.data.authorizedReports) || [];
        self.authorized = Array.isArray(list) ? list : [];
        self.ok = true;
      })
      .catch(() => {
        self.ok = false;
      })
      .then(() => {
        self.loading = false;
      });
    this.loadMembers();
    this.loadNews();
  },
  methods: {
    // Overlay any published rename onto the built-in titles. A METHOD, not a computed — it takes an
    // argument, and as a computed Vue evaluated it with list === undefined, so list.map threw and took
    // visibleReports down with it (the page then sat on "Loading your reports…"). Reading this.sharedNames
    // inside the call still makes visibleReports track it, so a rename re-renders.
    withNames(list) {
      const m = { "sales-insights": "ALL", "accounting-insights": "SHARED3" };
      const n = this.sharedNames || {};
      return list.map((r) => (m[r.key] && n[m[r.key]]) ? Object.assign({}, r, { title: n[m[r.key]] }) : r);
    },
    // Build the 👥 lists — a MIRROR of Account Setup → Report Access, per the user's explicit rule
    // (2026-07-31: "I need it to reflect what's in account setup"). all-insights members are NOT
    // folded in (they have access through the covering role, but they are not on this report's
    // list — the popup footer says so instead). The ONE exception stays: Sales Insights unions the
    // Territory Access members, because reps get that report through their territories.
    loadMembers() {
      const self = this;
      Promise.all([
        httpClient.get("/report/report-users"),
        httpClient.get("/report/territory-users")
      ])
        .then(([ru, tu]) => {
          const norm = (email) => String(email || "").trim().toLowerCase();
          // Which feed each address came from matters: a rep with a TERRITORY but no row on the Sales
          // Insights list looks like a member yet the report will not open for them (the report-access
          // list is what gates the page) — that gap gets flagged per member below.
          const own = {};    // report key -> { email: 1 } from the report's OWN access list
          const all = {};    // { email: 1 } from the ALL-INSIGHTS list — the covering role: these people
                             // hold (or get, at their next Microsoft sign-in) access to EVERY report
          const terr = {};   // { email: 1 } from Territory Access (any territory)
          const reportRows = (ru.data && (ru.data.reports || ru.data)) || [];
          const chip = {};   // report key -> true when the row carries the "All Insights" local-role chip
          (Array.isArray(reportRows) ? reportRows : []).forEach((row) => {
            const key = String(row.reportKey || "").trim().toLowerCase();
            const bucket = key === "all-insights" ? all : (own[key] = own[key] || {});
            (row.emails || []).forEach((e) => { const x = norm(e); if (x) bucket[x] = 1; });
            if ((row.groups || []).some((g) => String((g && g.id) || "").trim().toLowerCase() === "role:all insights")) chip[key] = true;
          });
          const terrRows = (tu.data && (tu.data.territories || tu.data)) || [];
          (Array.isArray(terrRows) ? terrRows : []).forEach((row) => {
            (row.emails || []).forEach((e) => { const x = norm(e); if (x) terr[x] = 1; });
          });
          // Three states per member: "own" = on this report's list; "all" = inherited from the All
          // Insights list (✓ — role granted at next sign-in), folded in ONLY when the report row itself
          // carries the All Insights chip (user 2026-07-31: a report WITHOUT the chip — e.g. Accounting —
          // shows exactly its own manual emails; the footer notes the covering roles instead); "warn" =
          // territory-only on Sales Insights with NO list row anywhere — the one genuinely broken case.
          const out = {};
          this.reports.forEach((r) => {
            const ownSet = own[r.key] || {};
            const tags = {};
            Object.keys(ownSet).forEach((e) => { tags[e] = "own"; });
            if (chip[r.key]) Object.keys(all).forEach((e) => { if (!tags[e]) tags[e] = "all"; });
            if (r.key === "sales-insights") Object.keys(terr).forEach((e) => { if (!tags[e]) tags[e] = "warn"; });
            out[r.key] = Object.keys(tags).sort().map((e) => ({ e, tag: tags[e] }));
          });
          self.members = out;
          self.membersOk = true;
        })
        .catch(() => { self.membersOk = false; });   // non-Master (403) or BE not deployed: no icons
    },
    memberWarns(key) { return (this.members[key] || []).filter((m) => m.tag === "warn").length; },
    memberAlls(key) { return (this.members[key] || []).filter((m) => m.tag === "all").length; },
    memberWarnList(key) { return (this.members[key] || []).filter((m) => m.tag === "warn").map((m) => m.e).join(", "); },
    // Copy the flagged addresses comma-separated — the exact shape the Report Access manual-emails
    // field takes, so fixing the gap is paste-and-Save. Clipboard API first (the portal is https);
    // execCommand fallback for anything older, and a prompt as the last resort so the text is never lost.
    copyWarnMembers(key) {
      const text = this.memberWarnList(key);
      if (!text) return;
      const self = this;
      const done = () => {
        self.memberCopied = key;
        setTimeout(() => { if (self.memberCopied === key) self.memberCopied = ""; }, 1800);
      };
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done, () => self._copyWarnFallback(text, done));
          return;
        }
      } catch (e) { /* fall through */ }
      this._copyWarnFallback(text, done);
    },
    _copyWarnFallback(text, done) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        done();
      } catch (e) { window.prompt("Copy these addresses:", text); }
    },
    openRequest() { this.reqOpen = true; this.reqDone = false; this.reqErr = ""; },
    closeRequest() {
      if (this.reqBusy) return;
      this.reqOpen = false;
      if (this.reqDone) this.reqMsg = "";   // a sent message is done; an unsent draft survives reopening
    },
    sendRequest() {
      const self = this;
      if (!this.reqMsg.trim() || this.reqBusy) return;
      this.reqBusy = true;
      this.reqErr = "";
      httpClient.post("/Email/report-request", {
        message: this.reqMsg.trim(),
        currentReports: this.visibleReports.map((r) => r.title).join(", "),
        pageUrl: (typeof window !== "undefined" && window.location) ? window.location.href : ""
      })
        .then(() => { self.reqDone = true; })
        .catch(() => { self.reqErr = "Couldn't send — please try again."; })
        .then(() => { self.reqBusy = false; });
    },
    loadNews() {
      const self = this;
      httpClient.get("/report/report-news")
        .then((res) => {
          const d = res.data || {};
          if (d.available === false) return;   // table not created yet — the baked-in fallback stays
          self.newsItems = Array.isArray(d.items) ? d.items : [];
          self.newsDb = true;
        })
        .catch(() => { /* fallback stays */ });
    },
    startNewsEdit(key, n) {
      this.newsErr = "";
      this.newsEdit = n
        ? { id: n.id, key, d: n.d || "", t: n.t, a: (n.a || []).slice() }
        : { id: null, key, d: new Date().toISOString().slice(0, 10), t: "", a: [] };
    },
    saveNews() {
      const self = this;
      const e = this.newsEdit;
      if (!e || !e.t.trim() || this.newsBusy) return;
      this.newsBusy = true;
      this.newsErr = "";
      httpClient.post("/report/report-news", {
        id: e.id, reportKey: e.key, newsDate: e.d || null, body: e.t.trim(),
        attachments: e.a && e.a.length ? JSON.stringify(e.a) : null
      })
        .then((res) => {
          const d = res.data || {};
          if (!d.ok) { self.newsErr = d.message || "Couldn't save."; return; }
          self.newsEdit = null;
          self.loadNews();
        })
        .catch(() => { self.newsErr = "Couldn't save — please try again."; })
        .then(() => { self.newsBusy = false; });
    },
    // × opens the styled "Are you sure?" confirm (user 2026-08-01 — was a plain window.confirm);
    // the actual delete is a SOFT delete server-side and lands in the 🗑 Deleted history.
    deleteNews(n) {
      if (!n || !n.id) return;
      this.newsDelConfirm = n;
    },
    confirmDeleteNews() {
      const n = this.newsDelConfirm;
      if (!n || !n.id || this.newsDelBusy) return;
      const self = this;
      this.newsDelBusy = true;
      httpClient.delete("/report/report-news/" + n.id)
        .then((res) => {
          if (res.data && res.data.ok) { self.newsDelConfirm = null; self.loadNews(); }
          else window.alert((res.data && res.data.message) || "Couldn't delete.");
        })
        .catch(() => window.alert("Couldn't delete — please try again."))
        .then(() => { self.newsDelBusy = false; });
    },
    // 🗑 Deleted history — list + restore, same gated editor as everything else here.
    openNewsTrash() {
      this.newsTrashOpen = true;
      this.loadNewsTrash();
    },
    loadNewsTrash() {
      const self = this;
      this.newsTrashBusy = true;
      this.newsTrashErr = "";
      httpClient.get("/report/report-news-deleted")
        .then((res) => {
          const d = res.data || {};
          if (d.ok) self.newsTrashItems = Array.isArray(d.items) ? d.items : [];
          else { self.newsTrashItems = []; self.newsTrashErr = d.message || "Couldn't load the Deleted history."; }
        })
        .catch(() => { self.newsTrashItems = []; self.newsTrashErr = "Couldn't load the Deleted history."; })
        .then(() => { self.newsTrashBusy = false; });
    },
    restoreNews(it) {
      if (!it || !it.id || this.newsTrashBusy) return;
      const self = this;
      this.newsTrashBusy = true;
      httpClient.post("/report/report-news-restore/" + it.id)
        .then((res) => {
          if (res.data && res.data.ok) { self.loadNews(); self.loadNewsTrash(); }
          else { self.newsTrashErr = (res.data && res.data.message) || "Couldn't restore."; self.newsTrashBusy = false; }
        })
        .catch(() => { self.newsTrashErr = "Couldn't restore — please try again."; self.newsTrashBusy = false; });
    },
    newsReportTitle(key) {
      const k = String(key || "").toLowerCase();
      const r = (this.reports || []).find((x) => x.key === k);
      return r ? r.title : key;
    },
    toggleNewsGroup(k) { this.$set(this.newsExpanded, k, !this.newsExpanded[k]); },
    // Stable per-item key. Saved rows have an id; the baked-in fallback list does not, so derive one
    // rather than letting every id-less item collide on undefined.
    nk(n) { return n && n.id ? "i" + n.id : "f" + ((n && n.d) || "") + "|" + String((n && n.t) || "").slice(0, 24); },
    // Decide which bodies need a "Read more" by MEASURING the clamped element, so a short item never
    // gets a pointless button and a long one is never silently truncated. Only writes on a real change,
    // which is what stops the watcher below from looping.
    measureNewsClamp() {
      const els = this.$refs.newsBody;
      if (!els) return;
      const list = Array.isArray(els) ? els : [els];
      list.forEach((el) => {
        if (!el || !el.dataset) return;
        const k = el.dataset.nk;
        if (!k || this.newsTextOpen[k]) return;   // expanded: not clamped, nothing to measure
        const over = el.scrollHeight > el.clientHeight + 1;
        if (!!this.newsTextClamped[k] !== over) this.$set(this.newsTextClamped, k, over);
      });
    },
    // ---- What's New attachments (paste a screenshot / 📎 any file) — helpdesk editor only -----------
    // Ctrl+V in the "What changed?" box: image pastes become attachments (same idea as the Account
    // Setup Replace-Charts paste); text pastes fall through untouched.
    onNewsPaste(e) {
      if (!this.newsEdit) return;
      var items = (e.clipboardData && e.clipboardData.items) || [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (it.kind === "file" && /^image\//i.test(it.type)) {
          var blob = it.getAsFile();
          if (blob) {
            e.preventDefault();
            this.uploadNewsAttachment(blob,
              "screenshot-" + new Date().toISOString().slice(0, 10) + (/jpeg/i.test(it.type) ? ".jpg" : ".png"));
          }
          return;
        }
      }
    },
    onNewsFilePick(e) {
      var files = (e.target && e.target.files) || [];
      for (var i = 0; i < files.length; i++) this.uploadNewsAttachment(files[i], files[i].name);
      if (e.target) e.target.value = "";   // same file re-pickable
    },
    // ---- Attachment preview modal (modal FIRST — Download / Open live inside it) --------------------
    openNewsPreview(a) {
      this.newsPreview = a;
      this.newsPvScale = 1; this.newsPvX = 0; this.newsPvY = 0;
      this._newsPvPan = null;
    },
    closeNewsPreview() { this.newsPreview = null; this._newsPvPan = null; },
    // Same multiplicative wheel feel as the Search chart viewer; pan resets when zoomed back to 1.
    newsPvWheel(e) {
      var f = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      this.newsPvScale = Math.min(6, Math.max(1, Math.round(this.newsPvScale * f * 100) / 100));
      if (this.newsPvScale === 1) { this.newsPvX = 0; this.newsPvY = 0; }
    },
    newsPvPanStart(e) { this._newsPvPan = { x: e.clientX - this.newsPvX, y: e.clientY - this.newsPvY }; },
    newsPvPanMove(e) {
      if (!this._newsPvPan) return;
      this.newsPvX = e.clientX - this._newsPvPan.x;
      this.newsPvY = e.clientY - this._newsPvPan.y;
    },
    newsPvPanEnd() { this._newsPvPan = null; },
    openNewsAttachmentTab(a) { if (a && a.url) window.open(a.url, "_blank", "noopener"); },
    // Real save-as: the blob container is cross-origin (no CORS), so fetch the bytes THROUGH the API
    // (which adds the download disposition) and hand them to a temporary object-URL anchor. If the
    // API path fails for any reason, fall back to opening the raw blob in a new tab.
    downloadNewsAttachment(a) {
      var self = this;
      if (!a || !a.url || this.newsPvBusy) return;
      this.newsPvBusy = true;
      httpClient.get("/report/report-news-file", { params: { url: a.url, name: a.name }, responseType: "blob" })
        .then(function (res) {
          var blobUrl = window.URL.createObjectURL(res.data);
          var el = document.createElement("a");
          el.href = blobUrl;
          el.download = a.name || "attachment";
          document.body.appendChild(el);
          el.click();
          document.body.removeChild(el);
          setTimeout(function () { window.URL.revokeObjectURL(blobUrl); }, 4000);
        })
        .catch(function () { window.open(a.url, "_blank", "noopener"); })
        .then(function () { self.newsPvBusy = false; });
    },
    uploadNewsAttachment(blob, name) {
      var self = this;
      if (!this.newsEdit) return;
      if (blob.size > 15 * 1024 * 1024) { this.newsErr = "Attachments are limited to 15 MB."; return; }
      var reader = new FileReader();
      reader.onload = function (ev) {
        self.newsAttBusy = true;
        self.newsErr = "";
        httpClient.post("/report/report-news-attachment", { fileName: name, dataUri: ev.target.result })
          .then(function (res) {
            var d = res.data || {};
            if (d.ok && d.url) {
              // The editor may have been cancelled while uploading — only attach if it's still open.
              if (self.newsEdit) self.newsEdit.a.push({ url: d.url, name: d.name || name, isImage: !!d.isImage });
            } else { self.newsErr = d.message || "Upload failed."; }
          })
          .catch(function () { self.newsErr = "Upload failed — please try again."; })
          .then(function () { self.newsAttBusy = false; });
      };
      reader.readAsDataURL(blob);
    },
    openReport(key) {
      // Standalone reports have their own route; others deep-link into Customer Insights.
      var standalone = {
        "manufacturing-insights": "/reports/manufacturing-insights",
        "foundry-insights": "/reports/foundry-insights",
        "work-order-insights": "/reports/work-order-insights",
      };
      if (standalone[key]) {
        this.$router.push({ path: standalone[key] }).catch(() => {});
        return;
      }
      this.$router
        .push({ path: "/reports/customer-insights", query: { report: key } })
        .catch(() => {});
    },
    // "Jul 31" this year, "Jul 31, 2025" otherwise — short enough for the date chip.
    fmtNewsDate(d) {
      try {
        var dt = new Date(d + "T00:00:00");
        var opts = { month: "short", day: "numeric" };
        if (dt.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
        return dt.toLocaleDateString(undefined, opts);
      } catch (e) { return d; }
    }
  }
};
</script>

<style scoped>
/* The /reports/ shell (steele-consistency-fix.css) owns the container width + top padding; the content sits
   in a centered branded panel with a brand-red top line. */
.rd-panel {
  /* Near-full report-container width — pulled in a touch from the rails on each side. */
  margin: 4px 16px 0;
  background: #fff;
  border: 1px solid #e4e9ee;
  border-top: 3px solid var(--steele-red, #ed1d24);
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  padding: 24px 24px 28px;
}
.rd-head {
  text-align: center;
  margin: 0 0 22px;
}
.rd-title {
  font-size: 26px;
  font-weight: 800;
  color: #2a2a2a;
  margin: 0 0 6px;
  letter-spacing: 0.01em;
}
.rd-sub {
  font-size: 14px;
  color: #5a636c;
  margin: 0 auto;
  max-width: 620px;
}
/* the "clicking here" link — a button styled as an inline link so it lives inside the sentence */
.rd-sub-link {
  display: inline;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 700;
  color: var(--steele-red, #ed1d24);
  text-decoration: underline;
  cursor: pointer;
}
/* the request modal — same visual grammar as the portal's helpdesk dialogs */
/* CENTERED in the viewport (user 2026-07-31) — the earlier top-anchored position tucked the modal
   title under the fixed header. */
.rd-req-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  background: rgba(20, 24, 31, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.rd-req-modal {
  width: 480px;
  max-width: 100%;
  background: #fff;
  border-radius: 10px;
  border-top: 3px solid var(--steele-red, #ed1d24);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.3);
  padding: 14px 18px 16px;
}
.rd-req-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 15px;
  font-weight: 800;
  color: #2a2a2a;
  margin-bottom: 6px;
}
.rd-req-x {
  border: 0;
  background: none;
  font-size: 14px;
  color: #8a929b;
  cursor: pointer;
}
.rd-req-sub { font-size: 13px; color: #5a636c; margin: 0 0 10px; }
.rd-req-text {
  width: 100%;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.5;
  padding: 10px 12px;
  border: 1px solid #d8dee5;
  border-radius: 8px;
  background: #fff;
  color: #2a2a2a;
  resize: vertical;
}
.rd-req-text:focus { outline: none; border-color: var(--steele-red, #ed1d24); }
.rd-req-err { font-size: 12.5px; font-weight: 700; color: var(--steele-red, #ed1d24); margin: 8px 0 0; }
.rd-req-done { font-size: 14px; font-weight: 700; color: #2f8f5b; margin: 8px 0 4px; }
.rd-req-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
.rd-req-cancel, .rd-req-send {
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  padding: 8px 18px;
  border-radius: 8px;
  cursor: pointer;
}
.rd-req-cancel { color: #5a636c; background: #f2f4f6; border: 1px solid #d8dee5; }
.rd-req-send { color: #fff; background: var(--steele-red, #ed1d24); border: 1px solid var(--steele-red, #ed1d24); }
.rd-req-send:disabled { opacity: .55; cursor: default; }
.rd-loading {
  padding: 40px 8px;
  font-size: 14px;
  color: #8a929b;
  text-align: center;
}
/* Two columns: report cards stacked on the LEFT, What's New filling the RIGHT. */
.rd-body {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}
.rd-list {
  flex: 0 0 400px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
/* Row-shaped card for the vertical list: icon · text · go-arrow. */
.rd-card {
  position: relative;   /* anchors the 👥 members popup */
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  width: 100%;
  text-align: left;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #e4e9ee;
  border-left: 4px solid var(--steele-red, #ed1d24);
  border-radius: 10px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.rd-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.10);
  transform: translateY(-2px);
}
.rd-card:focus-visible {
  outline: 2px solid var(--steele-red, #ed1d24);
  outline-offset: 2px;
}
/* The card whose 👥 popup is open lifts above its siblings — all cards are position:relative, so a
   LATER sibling otherwise paints over an EARLIER card's popup purely by DOM order. */
.rd-card.has-pop { z-index: 45; }
.rd-card-ic {
  font-size: 26px;
  line-height: 1;
  flex: 0 0 auto;
}
.rd-card-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
}
.rd-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #2a2a2a;
}
.rd-card-desc {
  font-size: 12.5px;
  color: #5a636c;
  line-height: 1.45;
}
.rd-card-go {
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 700;
  color: var(--steele-red, #ed1d24);
  white-space: nowrap;
}
/* 👥 who-can-see-this — icon in the card row; popup anchored to the card (position:relative below). */
.rd-mem-ic {
  flex: 0 0 auto;
  font-size: 17px;
  line-height: 1;
  padding: 4px 6px;
  border-radius: 8px;
  cursor: default;
  transition: background 0.12s ease;
}
.rd-mem-ic:hover { background: #f2f4f6; }
.rd-mem-pop {
  position: absolute;
  top: calc(100% - 8px);
  right: 10px;
  z-index: 40;
  width: 320px;
  max-width: calc(100% - 20px);
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #d8dee5;
  border-top: 3px solid var(--steele-red, #ed1d24);
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
  cursor: default;
  text-align: left;
}
.rd-mem-pop-h {
  font-size: 12.5px;
  font-weight: 800;
  color: #2a2a2a;
}
.rd-mem-pop-h em {
  font-style: normal;
  font-weight: 700;
  font-size: 11px;
  color: #6b7480;
  background: #f2f4f6;
  border-radius: 10px;
  padding: 1px 8px;
  margin-left: 6px;
}
.rd-mem-pop-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
  max-height: 220px;
  overflow-y: auto;
}
.rd-mem-pop-item {
  font-size: 12px;
  color: #444c55;
  line-height: 1.4;
  overflow-wrap: anywhere;
}
.rd-mem-pop-item.is-warn { color: #b45309; font-weight: 700; }
.rd-mem-warn-tag {
  font-style: normal;
  font-size: 10px;
  font-weight: 700;
  color: #92400e;
  background: #fdf3e0;
  border: 1px solid #e6b34a;
  border-radius: 9px;
  padding: 1px 7px;
  margin-left: 6px;
  white-space: nowrap;
  cursor: help;
}
.rd-mem-pop-warnfoot { color: #92400e; font-weight: 700; cursor: help; }
.rd-mem-all-tag {
  font-style: normal;
  font-size: 10px;
  font-weight: 700;
  color: #1d6f42;
  background: #e9f6ee;
  border: 1px solid #9fd4b4;
  border-radius: 9px;
  padding: 1px 7px;
  margin-left: 6px;
  white-space: nowrap;
  cursor: help;
}
.rd-mem-pop-allfoot { color: #1d6f42; font-weight: 700; }
.rd-mem-copy {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  font-size: 10px;
  font-weight: 700;
  font-family: inherit;
  color: #92400e;
  background: #fff;
  border: 1px solid #e6b34a;
  border-radius: 9px;
  cursor: pointer;
  white-space: nowrap;
}
.rd-mem-copy:hover { background: #e6b34a; color: #fff; }
.rd-mem-pop-none {
  font-size: 12px;
  color: #8a929b;
}
.rd-mem-pop-foot {
  font-size: 11px;
  color: #8a929b;
  border-top: 1px dashed #e4e9ee;
  padding-top: 6px;
}
/* What's New — a sibling branded card; grows with content (the page scrolls, not the card). */
.rd-news {
  flex: 1 1 auto;
  min-width: 0;
  background: #fff;
  border: 1px solid #e4e9ee;
  border-top: 3px solid var(--steele-red, #ed1d24);
  border-radius: 10px;
  padding: 18px 22px 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.rd-news-h {
  font-size: 18px;
  font-weight: 800;
  color: #2a2a2a;
}
.rd-news-sub {
  font-size: 12.5px;
  color: #8a929b;
  margin: 2px 0 4px;
}
.rd-news-group {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #e4e9ee;
}
.rd-news-group-h {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #2a2a2a;
}
.rd-news-group-ic {
  font-size: 17px;
  line-height: 1;
}
.rd-news-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 8px;
}
/* Every date chip is red-on-white — consistent down the whole list (user 2026-07-31; the earlier
   14-day "new" accent made older chips fade to gray). */
.rd-news-date {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: var(--steele-red, #ed1d24);
  border-radius: 10px;
  padding: 2px 9px;
  margin-top: 1px;
  white-space: nowrap;
}
.rd-news-text {
  font-size: 13px;
  color: #444c55;
  line-height: 1.5;
  min-width: 0;
}
/* 3-LINE CLAMP. -webkit-line-clamp is the only widely-supported multi-line clamp and is honoured by
   every current browser incl. Firefox, despite the vendor prefix. The element must be its own box
   (display:-webkit-box), which is why the body was split out of .rd-news-text. */
.rd-news-body { display: block; }
.rd-news-body.is-clamped {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.rd-news-readmore {
  display: inline-block;
  margin: 2px 0 0;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  color: var(--steele-red, #ed1d24);
  cursor: pointer;
}
.rd-news-readmore:hover { text-decoration: underline; }
.rd-news-none {
  margin: 14px 0 0;
  font-size: 13px;
  color: #8a929b;
}
/* ---- What's New attachments ------------------------------------------------------------------ */
.rd-news-fileinput { display: none; }
/* Editor: 📎 button + pending-attachment chips under the textarea. */
.rd-news-attrow { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 6px; }
.rd-news-clip {
  border: 1px solid #cfd6dd; background: #f6f8fa; color: #444c55; font-size: 11px; font-weight: 700;
  border-radius: 999px; padding: 3px 10px; cursor: pointer; white-space: nowrap;
}
.rd-news-clip:hover { background: #e9edf1; }
.rd-news-clip:disabled { opacity: .55; cursor: wait; }
.rd-news-attbusy { font-size: 11px; color: #8a929b; }
.rd-news-attchip {
  display: inline-flex; align-items: center; gap: 5px; max-width: 220px; font-size: 11px; color: #444c55;
  background: #fff; border: 1px solid #dfe5ea; border-radius: 999px; padding: 2px 4px 2px 3px;
}
.rd-news-attchipimg { width: 22px; height: 22px; object-fit: cover; border-radius: 999px; }
.rd-news-attchipname { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rd-news-attchipx { border: 0; background: none; color: #98a1aa; font-size: 13px; line-height: 1; cursor: pointer; padding: 0 3px; }
.rd-news-attchipx:hover { color: #c0392b; }
/* Read view: collapsed "📎 N attachments" toggle; expands to inline previews + file links. */
.rd-news-attwrap { display: block; margin-top: 4px; }
.rd-news-atttoggle {
  border: 1px solid #dfe5ea; background: #f6f8fa; color: #5a626b; font-size: 11px; font-weight: 700;
  border-radius: 999px; padding: 2px 9px; cursor: pointer; white-space: nowrap;
}
.rd-news-atttoggle:hover { background: #e9edf1; }
.rd-news-attlist { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 8px; margin-top: 7px; }
.rd-news-attimga { display: inline-block; line-height: 0; }
.rd-news-attimg {
  max-width: 260px; max-height: 170px; border: 1px solid #dfe5ea; border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .08); cursor: zoom-in;
}
.rd-news-attfile {
  display: inline-flex; align-items: center; font-size: 12px; font-weight: 700; color: #2c5aa0;
  background: #f4f7fb; border: 1px solid #d4dfec; border-radius: 999px; padding: 3px 10px; text-decoration: none;
  cursor: pointer; font-family: inherit;
}
.rd-news-attfile:hover { background: #e6edf6; text-decoration: none; }
/* Attachment preview modal — image gets the chart-viewer feel (zoom/pan); footer offers Download/Open. */
.rd-att-overlay {
  position: fixed; inset: 0; z-index: 100095; display: flex; align-items: center; justify-content: center;
  background: rgba(20, 18, 19, .55); padding: 18px;
}
.rd-att-modal {
  display: flex; flex-direction: column; width: min(920px, 94vw); max-height: 88vh;
  background: #fff; border-radius: 12px; border-top: 3px solid var(--steele-red, #ed1d24);
  box-shadow: 0 16px 48px rgba(0, 0, 0, .45); overflow: hidden;
}
.rd-att-head {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 11px 14px; border-bottom: 1px solid #eee; font-weight: 800; font-size: 14px; color: #2a2a2a;
}
.rd-att-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.rd-att-x { border: 0; background: none; font-size: 16px; line-height: 1; color: #888; cursor: pointer; padding: 2px 4px; }
.rd-att-x:hover { color: #333; }
.rd-att-body {
  flex: 1 1 auto; min-height: 220px; display: flex; align-items: center; justify-content: center;
  overflow: hidden; background: #f2f3f5; cursor: grab; user-select: none;
}
.rd-att-body:active { cursor: grabbing; }
.rd-att-img { max-width: 100%; max-height: 66vh; transition: transform .06s linear; will-change: transform; }
.rd-att-nofile { flex-direction: column; gap: 10px; cursor: default; font-size: 13px; color: #5a626b; padding: 30px 20px; text-align: center; }
.rd-att-bigicon { font-size: 42px; }
.rd-att-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 11px 14px; border-top: 1px solid #eee;
}
.rd-att-hint { font-size: 11px; color: #8a929b; }
.rd-att-btns { display: flex; align-items: center; gap: 10px; margin-left: auto; }
.rd-att-dl {
  border: 0; background: var(--steele-red, #ed1d24); color: #fff; font-weight: 800; font-size: 13px;
  border-radius: 8px; padding: 7px 16px; cursor: pointer;
}
.rd-att-dl:hover { filter: brightness(.9); }
.rd-att-dl:disabled { opacity: .6; cursor: wait; }
.rd-att-open {
  border: 1px solid #cfd6dd; background: #fff; color: #444c55; font-weight: 700; font-size: 13px;
  border-radius: 8px; padding: 7px 14px; cursor: pointer;
}
.rd-att-open:hover { background: #f2f2f2; }
/* Delete confirm + 🗑 Deleted history (reuse the rd-att overlay/head/foot chrome). */
.rd-confirm-modal {
  display: flex; flex-direction: column; width: min(520px, 94vw); max-height: 82vh;
  background: #fff; border-radius: 12px; border-top: 3px solid var(--steele-red, #ed1d24);
  box-shadow: 0 16px 48px rgba(0, 0, 0, .45); overflow: hidden;
}
.rd-confirm-body { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; padding: 14px 16px; }
.rd-confirm-text { font-size: 13px; color: #444c55; line-height: 1.5; }
.rd-confirm-atts { font-size: 12px; color: #5a626b; font-weight: 700; }
.rd-confirm-hint { font-size: 12px; color: #8a929b; }
.rd-news-trashbtn {
  border: 1px solid #cfd6dd; background: #f6f8fa; color: #5a626b; font-size: 11px; font-weight: 700;
  border-radius: 999px; padding: 4px 11px; cursor: pointer; white-space: nowrap; margin-left: auto; margin-right: 10px;
}
.rd-news-trashbtn:hover { background: #e9edf1; }
.rd-trash-modal { width: min(680px, 94vw); }
.rd-trash-body { flex: 1 1 auto; overflow-y: auto; padding: 10px 16px 14px; }
.rd-trash-note { font-size: 13px; color: #8a929b; margin: 8px 0; }
.rd-trash-item { padding: 9px 0; border-bottom: 1px solid #f0f2f4; }
.rd-trash-item:last-child { border-bottom: 0; }
.rd-trash-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.rd-trash-report { font-size: 12px; font-weight: 800; color: #2a2a2a; }
.rd-trash-when { font-size: 11px; color: #8a929b; }
.rd-trash-restore {
  margin-left: auto; border: 1px solid #9fd4b4; background: #e9f6ee; color: #1d6f42; font-size: 11px;
  font-weight: 800; border-radius: 999px; padding: 3px 11px; cursor: pointer; white-space: nowrap;
}
.rd-trash-restore:hover { background: #1d6f42; border-color: #1d6f42; color: #fff; }
.rd-trash-restore:disabled { opacity: .55; cursor: wait; }
.rd-trash-text { font-size: 13px; color: #444c55; line-height: 1.5; margin-top: 4px; }
.rd-news-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}
.rd-news-search {
  flex: 0 1 260px;
  min-width: 150px;
  font-size: 12.5px;
  font-family: inherit;
  padding: 7px 12px;
  border: 1px solid #d8dee5;
  border-radius: 8px;
  background: #fff;
  color: #2a2a2a;
}
.rd-news-search:focus { outline: none; border-color: var(--steele-red, #ed1d24); }
.rd-news-add {
  margin-left: auto;
  width: 22px;
  height: 22px;
  padding: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  color: #fff;
  background: var(--steele-red, #ed1d24);
  border: 0;
  border-radius: 6px;
  cursor: pointer;
}
.rd-news-act { flex: 0 0 auto; display: inline-flex; gap: 4px; margin-left: auto; }
.rd-news-ib {
  width: 22px;
  height: 22px;
  padding: 0;
  font-size: 12px;
  line-height: 1;
  color: #6b7480;
  background: #f2f4f6;
  border: 1px solid #d8dee5;
  border-radius: 6px;
  cursor: pointer;
}
.rd-news-ib:hover { color: #fff; background: var(--steele-red, #ed1d24); border-color: var(--steele-red, #ed1d24); }
.rd-news-more {
  margin: 8px 0 0;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 700;
  font-family: inherit;
  color: var(--steele-red, #ed1d24);
  background: transparent;
  border: 1px solid #e4b6b8;
  border-radius: 12px;
  cursor: pointer;
}
.rd-news-more:hover { background: var(--steele-red, #ed1d24); color: #fff; border-color: var(--steele-red, #ed1d24); }
/* inline editor (helpdesk only) */
.rd-news-editwrap { flex: 1 1 auto; display: flex; flex-direction: column; gap: 6px; }
.rd-news-erow { display: flex; align-items: center; gap: 8px; }
.rd-news-edate {
  font-size: 12px;
  font-family: inherit;
  padding: 4px 8px;
  border: 1px solid #d8dee5;
  border-radius: 6px;
  background: #fff;
  color: #2a2a2a;
}
.rd-news-ebtns { margin-left: auto; display: inline-flex; gap: 6px; }
.rd-news-esave, .rd-news-ecancel {
  font-size: 12px;
  font-weight: 700;
  font-family: inherit;
  padding: 4px 12px;
  border-radius: 6px;
  cursor: pointer;
}
.rd-news-esave { color: #fff; background: var(--steele-red, #ed1d24); border: 1px solid var(--steele-red, #ed1d24); }
.rd-news-esave:disabled { opacity: .55; cursor: default; }
.rd-news-ecancel { color: #5a636c; background: #f2f4f6; border: 1px solid #d8dee5; }
.rd-news-etext {
  width: 100%;
  font-size: 12.5px;
  font-family: inherit;
  line-height: 1.5;
  padding: 8px 10px;
  border: 1px solid #d8dee5;
  border-radius: 8px;
  background: #fff;
  color: #2a2a2a;
  resize: vertical;
}
.rd-news-etext:focus, .rd-news-edate:focus { outline: none; border-color: var(--steele-red, #ed1d24); }
.rd-news-err { font-size: 12px; color: var(--steele-red, #ed1d24); font-weight: 700; }
.rd-empty {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 22px;
  background: #f7f9fb;
  border: 1px solid #e4e9ee;
  border-radius: 10px;
  max-width: 560px;
  margin: 0 auto;
}
.rd-empty-ic {
  font-size: 26px;
}
.rd-empty p {
  margin: 0;
  font-size: 14px;
  color: #5a636c;
}

/* Dark mode — the portal toggles html.dark-mode globally. */
html.dark-mode .rd-panel {
  background: #1f242b;
  border-color: #3a414a;
  border-top-color: var(--steele-red, #ed1d24);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}
html.dark-mode .rd-title,
html.dark-mode .rd-card-title,
html.dark-mode .rd-news-h,
html.dark-mode .rd-news-group-h {
  color: #e8ebee;
}
html.dark-mode .rd-sub,
html.dark-mode .rd-card-desc,
html.dark-mode .rd-empty p {
  color: #aab3bd;
}
html.dark-mode .rd-card,
html.dark-mode .rd-news {
  background: #232830;
  border-color: #3a414a;
}
html.dark-mode .rd-news {
  border-top-color: var(--steele-red, #ed1d24);
}
html.dark-mode .rd-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
}
html.dark-mode .rd-news-group {
  border-top-color: #3a414a;
}
html.dark-mode .rd-news-text {
  color: #c3cad2;
}
html.dark-mode .rd-news-clip,
html.dark-mode .rd-news-atttoggle { background: #2b313a; border-color: #3a414a; color: #c3cad2; }
html.dark-mode .rd-news-clip:hover,
html.dark-mode .rd-news-atttoggle:hover { background: #343b45; }
html.dark-mode .rd-news-attchip { background: #232830; border-color: #3a414a; color: #c3cad2; }
html.dark-mode .rd-news-attimg { border-color: #3a414a; }
html.dark-mode .rd-news-attfile { background: #232e3d; border-color: #34455c; color: #8fb4e8; }
html.dark-mode .rd-news-attfile:hover { background: #2a3749; }
html.dark-mode .rd-att-modal { background: #232830; }
html.dark-mode .rd-att-head { border-bottom-color: #3a414a; color: #e7e9ec; }
html.dark-mode .rd-att-body { background: #1b1f25; }
html.dark-mode .rd-att-nofile { color: #c3cad2; }
html.dark-mode .rd-att-foot { border-top-color: #3a414a; }
html.dark-mode .rd-att-open { background: #2b313a; border-color: #3a414a; color: #c3cad2; }
html.dark-mode .rd-att-open:hover { background: #343b45; }
html.dark-mode .rd-confirm-modal { background: #232830; }
html.dark-mode .rd-confirm-text,
html.dark-mode .rd-trash-text { color: #c3cad2; }
html.dark-mode .rd-news-trashbtn { background: #2b313a; border-color: #3a414a; color: #c3cad2; }
html.dark-mode .rd-news-trashbtn:hover { background: #343b45; }
html.dark-mode .rd-trash-item { border-bottom-color: #3a414a; }
html.dark-mode .rd-trash-report { color: #e7e9ec; }
html.dark-mode .rd-trash-restore { background: #16281d; border-color: #2f7d52; color: #6fce9a; }
html.dark-mode .rd-trash-restore:hover { background: #2f7d52; color: #fff; }
/* .rd-news-date needs no dark override — red/white reads on both themes */
html.dark-mode .rd-news-search,
html.dark-mode .rd-news-edate,
html.dark-mode .rd-news-etext {
  background: #1a1f26;
  border-color: #3a414a;
  color: #e8ebee;
}
html.dark-mode .rd-news-ib { background: #2c333c; border-color: #3a414a; color: #9aa3ad; }
html.dark-mode .rd-news-ib:hover { color: #fff; background: var(--steele-red, #ed1d24); border-color: var(--steele-red, #ed1d24); }
html.dark-mode .rd-news-more { border-color: #5a3236; }
html.dark-mode .rd-news-readmore { color: #f0a8b1; }
html.dark-mode .rd-news-ecancel { background: #2c333c; border-color: #3a414a; color: #aab3bd; }
html.dark-mode .rd-req-modal { background: #232830; }
html.dark-mode .rd-req-head { color: #e8ebee; }
html.dark-mode .rd-req-sub { color: #aab3bd; }
html.dark-mode .rd-req-text { background: #1a1f26; border-color: #3a414a; color: #e8ebee; }
html.dark-mode .rd-req-cancel { background: #2c333c; border-color: #3a414a; color: #aab3bd; }
html.dark-mode .rd-empty {
  background: #232830;
  border-color: #3a414a;
}
html.dark-mode .rd-mem-ic:hover { background: #2c333c; }
html.dark-mode .rd-mem-pop {
  background: #232830;
  border-color: #3a414a;
  border-top-color: var(--steele-red, #ed1d24);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.55);
}
html.dark-mode .rd-mem-pop-h { color: #e8ebee; }
html.dark-mode .rd-mem-pop-h em { background: #2c333c; color: #9aa3ad; }
html.dark-mode .rd-mem-pop-item { color: #c3cad2; }
html.dark-mode .rd-mem-pop-item.is-warn { color: #e0b578; }
html.dark-mode .rd-mem-warn-tag { background: #2a2116; border-color: #4a3a22; color: #e0b578; }
html.dark-mode .rd-mem-pop-warnfoot { color: #e0b578; }
html.dark-mode .rd-mem-all-tag { background: #16281d; border-color: #2f7d52; color: #6fce9a; }
html.dark-mode .rd-mem-pop-allfoot { color: #6fce9a; }
html.dark-mode .rd-mem-copy { background: #2a2116; border-color: #4a3a22; color: #e0b578; }
html.dark-mode .rd-mem-copy:hover { background: #4a3a22; color: #fff; }
html.dark-mode .rd-mem-pop-none, html.dark-mode .rd-mem-pop-foot { color: #8a929b; }
html.dark-mode .rd-mem-pop-foot { border-top-color: #3a414a; }

/* Stack below the desktop breakpoint: cards first, news below, both full width. */
@media (max-width: 991px) {
  .rd-body { flex-direction: column; }
  .rd-list { flex: 1 1 auto; width: 100%; }
  .rd-news { width: 100%; }
}
@media (max-width: 575px) {
  .rd-panel { padding: 18px 14px 22px; }
}
</style>
